/**
 * 검차 사진 업로드 컴포넌트
 * 
 * 카테고리별 사진 촬영/업로드:
 * - 외관 (전면/후면/좌측/우측/상면)
 * - 사고부위 (도막측정기, 판금흔적, 용접부위, 부식 등)
 * - 내관 (운전석/조수석/뒷좌석/트렁크/계기판)
 * - 하부 (엔진룸/하부/타이어/배기)
 * - 서류 (성능점검기록부/보험이력 등)
 * 
 * Props:
 *   photos: { [category]: [{ file, preview, memo }] }
 *   onChange: (photos) => void
 *   readOnly: boolean
 */
import React, { useState, useRef } from 'react';
import CustomModal from './CustomModal';

// 영상 파일 판정 (확장자/MIME)
var VIDEO_EXT_RE = /\.(mp4|mov|avi|webm|3gp|m4v|mkv)$/i;
function isVideoPhoto(p) {
  if (!p) return false;
  if (p.file && p.file.type && p.file.type.indexOf('video/') === 0) return true;
  if (VIDEO_EXT_RE.test(p.filename || '')) return true;
  if (VIDEO_EXT_RE.test(p.url || '')) return true;
  if (VIDEO_EXT_RE.test(p.preview || '')) return true;
  return false;
}
var MAX_FILE_BYTES = 50 * 1024 * 1024;
var ENGINE_VIDEO_MAX_SEC = 10;

const CATEGORIES = [
  {
    key: 'exterior',
    label: '외관',
    color: '#3B8BD4',
    bg: '#E6F1FB',
    guides: ['전면', '후면', '좌측면', '우측면', '좌전방 45도', '우전방 45도'],
  },
  {
    key: 'damage',
    label: '사고부위',
    color: '#E24B4A',
    bg: '#FCEBEB',
    guides: ['도막측정기', '판금흔적', '용접부위', '도장차이', '부식/녹', '기타손상'],
  },
  {
    key: 'interior',
    label: '내관',
    color: '#1D9E75',
    bg: '#E1F5EE',
    guides: ['운전석', '조수석', '뒷좌석', '계기판/주행거리', '트렁크', '천장/시트상태'],
  },
  {
    key: 'undercarriage',
    label: '하부/엔진',
    color: '#BA7517',
    bg: '#FAEEDA',
    guides: ['엔진룸', '엔진음 동영상', '하부 전체', '타이어/휠', '배기관', '누유부위'],
  },
  {
    key: 'documents',
    label: '서류',
    color: '#534AB7',
    bg: '#EEEDFE',
    guides: ['성능점검기록부', '보험이력', '차량등록증', '기타서류'],
  },
];

export default function InspectionPhotos({ photos = {}, onChange, readOnly = false }) {
  const [activeCategory, setActiveCategory] = useState('exterior');
  const [viewPhoto, setViewPhoto] = useState(null);
  const fileRef = useRef(null);
  const [captureGuide, setCaptureGuide] = useState(null);
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };

  var [uploadToast, setUploadToast] = useState(null);

  const category = CATEGORIES.find(c => c.key === activeCategory);
  const categoryPhotos = photos[activeCategory] || [];
  const totalPhotos = Object.values(photos).reduce((sum, arr) => sum + (arr?.length || 0), 0);
  var MAX_PHOTOS = 50;

  var showUploadToast = function(msg) {
    setUploadToast(msg);
    setTimeout(function() { setUploadToast(null); }, 2000);
  };

  // 사진 추가
  const handleAddPhoto = (guide) => {
    if (readOnly) return;
    if (totalPhotos >= MAX_PHOTOS) {
      setModal({ type: 'danger', title: '사진 수 초과', message: '사진은 최대 ' + MAX_PHOTOS + '장까지 등록 가능합니다. (현재 ' + totalPhotos + '장)', hideCancel: true, onConfirm: closeModal });
      return;
    }
    setCaptureGuide(guide);
    setTimeout(() => fileRef.current?.click(), 50);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    for (var i = 0; i < files.length; i++) {
      if (files[i].size > MAX_FILE_BYTES) {
        setModal({ type: 'danger', title: '실패', message: '파일은 50MB 이하만 업로드 가능합니다', hideCancel: true, onConfirm: closeModal });
        if (fileRef.current) fileRef.current.value = '';
        return;
      }
    }

    // 영상 길이 경고 (엔진음 동영상 권장 10초). 업로드는 허용하되 토스트로 주의.
    files.forEach(function(f) {
      if (f.type && f.type.indexOf('video/') === 0) {
        try {
          var tmpUrl = URL.createObjectURL(f);
          var v = document.createElement('video');
          v.preload = 'metadata';
          v.src = tmpUrl;
          v.onloadedmetadata = function() {
            if (v.duration > ENGINE_VIDEO_MAX_SEC + 0.5) {
              showUploadToast('영상 ' + Math.round(v.duration) + '초 — 10초 이내 권장');
            }
            URL.revokeObjectURL(tmpUrl);
          };
        } catch(e) {}
      }
    });

    // 최대 장수 제한
    var remaining = MAX_PHOTOS - totalPhotos;
    var allowedFiles = remaining >= files.length ? files : files.slice(0, remaining);
    if (allowedFiles.length < files.length) {
      setModal({ type: 'warning', title: '일부만 추가', message: '최대 ' + MAX_PHOTOS + '장 제한으로 ' + allowedFiles.length + '장만 추가됩니다.', hideCancel: true, onConfirm: closeModal });
    }

    const newPhotos = allowedFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      preview: URL.createObjectURL(file),
      memo: captureGuide || '',
      timestamp: new Date().toLocaleTimeString(),
    }));

    const updated = {
      ...photos,
      [activeCategory]: [...categoryPhotos, ...newPhotos],
    };
    onChange?.(updated);
    setCaptureGuide(null);
    if (fileRef.current) fileRef.current.value = '';
    showUploadToast(category.label + ' ' + newPhotos.length + '장 추가 완료');
  };

  // 사진 삭제
  const handleDeletePhoto = (photoId) => {
    if (readOnly) return;
    const updated = {
      ...photos,
      [activeCategory]: categoryPhotos.filter(p => p.id !== photoId),
    };
    onChange?.(updated);
  };

  // 메모 수정
  const handleMemoChange = (photoId, memo) => {
    const updated = {
      ...photos,
      [activeCategory]: categoryPhotos.map(p => p.id === photoId ? { ...p, memo } : p),
    };
    onChange?.(updated);
  };

  return (
    <div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/*"
        multiple
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* 업로드 완료 토스트 */}
      {uploadToast && (
        <div style={{
          position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
          background: '#1D9E75', color: '#fff', padding: '10px 24px',
          borderRadius: 10, fontSize: 14, fontWeight: 600, zIndex: 9999,
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        }}>{uploadToast}</div>
      )}

      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#222' }}>
          검차 사진
          {totalPhotos > 0 && (
            <span style={{ fontSize: 12, fontWeight: 400, color: totalPhotos >= MAX_PHOTOS ? '#E24B4A' : '#888', marginLeft: 6 }}>
              {totalPhotos}/{MAX_PHOTOS}장
            </span>
          )}
        </div>
      </div>

      {/* 카테고리 탭 */}
      <div style={{
        display: 'flex', gap: 0, borderRadius: 10, overflow: 'hidden',
        border: '1px solid #e0e0da', marginBottom: 12,
      }}>
        {CATEGORIES.map(cat => {
          const count = (photos[cat.key] || []).length;
          const isActive = activeCategory === cat.key;
          return (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              style={{
                flex: 1, padding: '8px 2px', border: 'none', cursor: 'pointer',
                fontSize: 10, fontWeight: isActive ? 700 : 400,
                color: isActive ? cat.color : '#888',
                background: isActive ? cat.bg : '#fff',
                borderBottom: isActive ? `2px solid ${cat.color}` : '2px solid transparent',
                position: 'relative',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {cat.label}
              {count > 0 && (
                <span style={{
                  position: 'absolute', top: 2, right: 4,
                  fontSize: 9, fontWeight: 700, color: '#fff',
                  background: cat.color, borderRadius: 8,
                  padding: '1px 5px', minWidth: 14, textAlign: 'center',
                }}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* 촬영 가이드 / 일괄 업로드 */}
      {!readOnly && category && (
        <div style={{ marginBottom: 12 }}>
          {activeCategory === 'exterior' ? (
            <div>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
                전면/후면/좌우측 등 외관 사진을 한번에 업로드하세요
              </div>
              <button
                onClick={() => handleAddPhoto('')}
                style={{
                  width: '100%', padding: '16px 0', borderRadius: 10,
                  border: '2px dashed ' + category.color, background: category.bg,
                  cursor: 'pointer', fontSize: 14, fontWeight: 700, color: category.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <span style={{ fontSize: 22 }}>📷</span>
                외관 사진 촬영/선택
              </button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
                촬영 가이드 — 터치하면 카메라가 열려요
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                {category.guides.map(guide => {
                  const hasPhoto = categoryPhotos.some(p => p.memo === guide);
                  return (
                    <button
                      key={guide}
                      onClick={() => handleAddPhoto(guide)}
                      style={{
                        padding: '10px 6px', borderRadius: 10, cursor: 'pointer',
                        border: hasPhoto ? `1.5px solid ${category.color}` : '1px dashed #ccc',
                        background: hasPhoto ? `${category.color}08` : '#fafaf8',
                        fontSize: 12, color: hasPhoto ? category.color : '#888',
                        fontWeight: hasPhoto ? 600 : 400,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <span style={{ fontSize: 18 }}>{hasPhoto ? '✓' : (guide.includes('동영상') ? '🎥' : '📷')}</span>
                      {guide}
                      {guide.includes('동영상') && <span style={{ fontSize: 9, color: '#aaa' }}>10초 이내 권장</span>}
                    </button>
                  );
                })}
              </div>
              {/* 자유 촬영 */}
              <button
                onClick={() => handleAddPhoto('')}
                style={{
                  width: '100%', marginTop: 6, padding: '10px 0', borderRadius: 10,
                  border: '1px dashed #ccc', background: '#fafaf8',
                  cursor: 'pointer', fontSize: 13, color: '#888',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                + 추가 촬영
              </button>
            </div>
          )}
        </div>
      )}

      {/* 사진 그리드 */}
      {categoryPhotos.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 12,
        }}>
          {categoryPhotos.map(photo => (
            <div key={photo.id} style={{ position: 'relative' }}>
              {isVideoPhoto(photo)
                ? <video
                    src={photo.preview || photo.url}
                    onClick={() => setViewPhoto(photo)}
                    controls
                    playsInline
                    style={{
                      width: '100%', aspectRatio: '1', objectFit: 'cover',
                      borderRadius: 10, border: '1px solid #eee', cursor: 'pointer',
                      background: '#000',
                    }}
                  />
                : <img
                    src={photo.preview || photo.url}
                    alt={photo.memo}
                    loading="lazy"
                    decoding="async"
                    onClick={() => setViewPhoto(photo)}
                    style={{
                      width: '100%', aspectRatio: '1', objectFit: 'cover',
                      borderRadius: 10, border: '1px solid #eee', cursor: 'pointer',
                    }}
                  />
              }
              {/* 메모 라벨 */}
              {photo.memo && (
                <div style={{
                  position: 'absolute', bottom: 4, left: 4, right: 4,
                  background: 'rgba(0,0,0,0.6)', color: '#fff',
                  fontSize: 9, padding: '2px 6px', borderRadius: 6,
                  textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}>
                  {photo.memo}
                </div>
              )}
              {/* 삭제 버튼 */}
              {!readOnly && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                  style={{
                    position: 'absolute', top: 4, right: 4,
                    width: 22, height: 22, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.5)', color: '#fff',
                    border: 'none', cursor: 'pointer', fontSize: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1,
                  }}
                >×</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 빈 상태 */}
      {categoryPhotos.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '24px 0', color: '#ccc', fontSize: 13,
        }}>
          {readOnly ? '등록된 사진이 없습니다' : '위 가이드를 터치해서 촬영하세요'}
        </div>
      )}

      {/* 전체 카테고리 사진 수 요약 */}
      {totalPhotos > 0 && (
        <div style={{
          background: '#f8f8f5', borderRadius: 10, padding: '8px 14px',
          display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4,
        }}>
          {CATEGORIES.map(cat => {
            const count = (photos[cat.key] || []).length;
            return (
              <span key={cat.key} style={{
                fontSize: 11, color: count > 0 ? cat.color : '#ccc',
                fontWeight: count > 0 ? 600 : 400,
              }}>
                {cat.label} {count}장
              </span>
            );
          })}
        </div>
      )}

      {/* 사진 확대 보기 */}
      {viewPhoto && (
        <div
          onClick={() => setViewPhoto(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)',
            zIndex: 9999, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', padding: 16,
            cursor: 'pointer',
          }}
        >
          {isVideoPhoto(viewPhoto)
            ? <video
                src={viewPhoto.preview || viewPhoto.url}
                controls
                autoPlay
                playsInline
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain',
                  borderRadius: 8, background: '#000',
                }}
              />
            : <img
                src={viewPhoto.preview || viewPhoto.url}
                alt={viewPhoto.memo}
                style={{
                  maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain',
                  borderRadius: 8,
                }}
              />
          }
          {viewPhoto.memo && (
            <div style={{
              color: '#fff', fontSize: 14, marginTop: 12,
              background: 'rgba(255,255,255,0.15)', padding: '6px 16px',
              borderRadius: 8,
            }}>
              {viewPhoto.memo}
            </div>
          )}
          {/* 메모 수정 (편집 모드) */}
          {!readOnly && (
            <input
              value={viewPhoto.memo}
              onChange={(e) => {
                handleMemoChange(viewPhoto.id, e.target.value);
                setViewPhoto(prev => ({ ...prev, memo: e.target.value }));
              }}
              onClick={(e) => e.stopPropagation()}
              placeholder="사진 메모 입력..."
              style={{
                marginTop: 10, padding: '8px 14px', borderRadius: 8,
                border: 'none', background: 'rgba(255,255,255,0.2)',
                color: '#fff', fontSize: 14, textAlign: 'center',
                width: '80%', maxWidth: 300, outline: 'none',
              }}
            />
          )}
          <div style={{ color: '#888', fontSize: 12, marginTop: 8 }}>
            터치하면 닫힘
          </div>
        </div>
      )}
      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
    </div>
  );
}
